<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0" style="color: #1f2937;">
        <v-icon class="mr-2" style="color: #6b7280;">mdi-factory</v-icon>
        Industry Matches
      </h3>
    </v-card-title>
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0" style="color: #6b7280;">Top opportunities matching your industry with contract start dates in the past 2 weeks</p>
    </div>

    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </div>

    <div v-else-if="!matches || matches.length === 0" class="text-center py-8 grey--text">
      No industry matches found
    </div>

    <v-card-text v-else class="pa-0">
      <div 
        v-for="(match, index) in paginatedMatches" 
        :key="`match-${match.tender_id}-${index}`"
        class="match-card pa-6"
        :class="{ 'match-card-border-top': index > 0 }"
        @click="handleMatchClick(match)"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <div class="d-flex align-center gap-2 mb-2">
              <v-chip 
                v-if="match.matching_industry" 
                x-small 
                style="background-color: #e6f0f5; color: #12253a; font-weight: 500; height: 22px; font-size: 11px;"
              >
                {{ match.matching_industry }}
              </v-chip>
              <v-chip 
                v-if="match.disposition" 
                x-small 
                :color="getDispositionColor(match.disposition)" 
                text-color="white" 
                style="height: 22px; font-size: 11px;"
              >
                {{ match.disposition }}
              </v-chip>
            </div>
            
            <router-link 
              :to="`/tender/${match.tender_id}`" 
              class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-3"
              style="color: #1f2937;"
              target="_blank"
            >
              {{ match.title }}
              <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
            </router-link>

            <div class="d-flex align-center gap-4 text-body-2 grey--text mb-3">
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                <router-link 
                  v-if="match.publisher_id" 
                  :to="`/entity/${match.publisher_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ match.publisher_name }}
                </router-link>
                <span v-else>{{ match.publisher_name }}</span>
              </span>
              <span v-if="match.supplier_name" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-domain</v-icon>
                <router-link 
                  v-if="match.supplier_id" 
                  :to="`/supplier/${match.supplier_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ match.supplier_name }}
                </router-link>
                <span v-else>{{ match.supplier_name }}</span>
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-cash</v-icon>
                {{ formatCurrency(match.value || 0) }}
              </span>
              <span v-if="match.contract_start" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                Contract Start: {{ formatDate(match.contract_start) }}
              </span>
            </div>

            <div v-if="match.category" class="text-body-2" style="color: #6b7280; font-size: 12px;">
              Category: {{ match.category }}
            </div>
          </div>

          <v-btn
            small
            depressed
            dark
            class="ml-4 body-2 text-capitalize"
            color="#3b82f6"
            text-color="white"
            style="font-size: 13px;"
            :to="`/tender/${match.tender_id}`"
            target="_blank"
          >
            <v-icon small left>mdi-eye</v-icon>
            View
          </v-btn>
        </div>
      </div>

      <!-- Pagination -->
      <v-card-actions 
        v-if="showPagination" 
        class="pa-4" 
        style="border-top: 1px solid #e5e7eb; background-color: #f9fafb;"
      >
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div class="text-body" style="color: #6b7280;">
            Showing {{ paginationStart }} - {{ paginationEnd }} of {{ matches.length }}
          </div>
          <div class="d-flex gap-2">
            <v-btn
              icon
              x-small
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="currentPage >= totalPages"
              @click="goToNextPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'IndustryMatchesSection',
  
  props: {
    matches: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      currentPage: 1
    };
  },

  computed: {
    paginatedMatches() {
      if (!this.matches || this.matches.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.matches.slice(start, end);
    },

    totalPages() {
      if (!this.matches || this.matches.length === 0) return 0;
      return Math.ceil(this.matches.length / this.itemsPerPage);
    },

    showPagination() {
      return this.matches && this.matches.length > this.itemsPerPage;
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.matches.length);
    }
  },

  watch: {
    matches() {
      // Reset to first page when matches change
      this.currentPage = 1;
    }
  },

  methods: {
    handleMatchClick(match) {
      if (match.tender_id) {
        this.$router.push(`/tender/${match.tender_id}`);
      }
    },

    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    formatCurrency(value) {
      if (!value) return "$0";
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    },

    getDispositionColor(disposition) {
      if (disposition === 'Request' || disposition === 'Tender') return '#3b82f6';
      if (disposition === 'Contract Notice') return '#10b981';
      if (disposition === 'Grant' || disposition === 'Grant Opportunity') return '#8b5cf6';
      return '#6b7280';
    }
  }
};
</script>

<style scoped>
.match-card {
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f0f9ff;
  border-left: 3px solid #3b82f6;
  transition: background-color 0.2s ease;
}

.match-card:hover {
  background-color: #dbeafe;
}

.match-card-border-top {
  border-top: 1px solid #e5e7eb;
}
</style>
